<script setup lang="ts">
import { computed } from "vue";
import { englishSecondaryName, FLAG_IMAGE_CLASS } from "./locale";

defineOptions({
  name: "VLocaleOption",
});

const {
  locale,
  name,
  flagSrc = null,
  secondary = undefined,
  countries = false,
  label = undefined,
  highlight = undefined,
} = defineProps<{
  /** Locale code the option stands for. */
  locale: string
  /** The language (or country) in its own name, as the closed field shows it. */
  name: string
  /** Source of the flag image. Without one no flag is drawn. */
  flagSrc?: string | null
  /** The small grey line above the name. Defaults to the English name, left out when it equals `name`. */
  secondary?: string | null
  countries?: boolean
  /** `name` as escaped HTML with the search matches marked, from the select's item slot. */
  label?: string
  /** Marks the search matches in a text, from the select's item slot. */
  highlight?: (text: unknown) => string
}>();

const secondaryText = computed(() => {
  if (secondary !== undefined) {
    return secondary;
  }
  return englishSecondaryName(locale, name, countries);
});
const secondaryHtml = computed(() => {
  if (secondaryText.value === null || !highlight) {
    return "";
  }
  return highlight(secondaryText.value);
});
</script>

<template>
  <div class="flex items-center gap-2 min-w-0 w-full">
    <img
      v-if="flagSrc"
      :class="FLAG_IMAGE_CLASS"
      :src="flagSrc"
      alt=""
    >
    <div class="flex flex-col gap-0.5 min-w-0 w-full leading-tight">
      <span
        v-if="secondaryText && secondaryHtml"
        class="text-xs text-gray-500 truncate"
        data-test="locale-option-secondary"
        v-html="secondaryHtml"
      />
      <span
        v-else-if="secondaryText"
        class="text-xs text-gray-500 truncate"
        data-test="locale-option-secondary"
      >
        {{ secondaryText }}
      </span>
      <span v-if="label !== undefined" class="truncate" data-test="locale-option-name" v-html="label" />
      <span v-else class="truncate" data-test="locale-option-name">{{ name }}</span>
    </div>
  </div>
</template>
